// @flow
import React, { useEffect, useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import Typography from '@material-ui/core/Typography';
import { confirm, showAlert } from 'actions/app';
import { StatusManager } from 'common/components/StatusManager';
import { useFetchWithStatus } from 'hooks/fetchWithStatus';
import { EntityRoutes } from 'routes/constants';
import { LinkHiringAuthorityOrCandidate } from 'services/Candidates';
import strings from 'strings';
import type { DrawerUiState } from 'types/app';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { Endpoints } from 'UI/constants/endpoints';
import { EntityType } from 'UI/constants/entityTypes';
import { OptionRenderers, Renderers } from 'UI/utils/renderers';

import { useStyles } from '../../styles';

const fieldsConfig = [
  {
    name: 'candidateId',
    validation: { required: 'Candidate is required' }
  }
];

type LinkCandidateProps = {
  hiringAuthorityId: number,
  onClose: () => any,
  onCompleted: () => any
};

const candidateIncludes = 'specialty,subspecialty,position';

const LinkCandidate = ({ hiringAuthorityId, onClose, onCompleted }: LinkCandidateProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [uiState, setUiState] = useState<DrawerUiState>({
    isSaving: false,
    navStack: []
  });

  const { state } = useFetchWithStatus(
    hiringAuthorityId
      ? `${Endpoints.HiringAuthorities}/${hiringAuthorityId}?includes=${candidateIncludes}`
      : '',
    null,
    null,
    2
  );

  const { status } = state;

  const hiringAuthority = state?.results;

  const form = useForm();
  const { handleSubmit, register, unregister, setValue, errors } = form;
  const [comboValues, setComboValues] = useState<Map>({});

  useEffect(() => {
    fieldsConfig.forEach(({ name, type, validation }) => {
      register({ name, type }, validation);
    });

    return () => unregister(fieldsConfig.map(({ name }) => name));
  }, [register, unregister]);

  const handleComboChange = (name?: string, value: any) => {
    setComboValues((prevState: Map): Map => ({ ...prevState, [name]: value }));
    setValue(name, value ? value.id : value, true);
  };

  const createHiringAuthority = async (formData = null) => {
    setUiState(prevState => ({ ...prevState, isSaving: true }));

    const { candidateId } = formData;
    const url = `${Endpoints.HiringAuthorities}/${hiringAuthorityId}/candidates/${candidateId}/link`;
    const result = await LinkHiringAuthorityOrCandidate(url, formData, {
      successTitle: 'Candidate Link Successfully',
      successBody: `${hiringAuthority?.full_name} was linked from the Hiring Authority Profile`
    });

    setUiState(prevState => ({ ...prevState, isSaving: false }));
    result.alert && result.alert.body && dispatch(showAlert(result.alert));
    result.success && onCompleted && onCompleted(result.data);
    result.success && onClose && onClose();
  };

  const onSubmit = async (formData: any) => {
    const candidateName = comboValues?.candidateId?.title;
    const hiringName = hiringAuthority?.full_name;

    dispatch(
      confirm({
        severity: 'warning',
        title: strings.inventoryProfiles.sections.candidates.linkCandidateDialog.title,
        message: strings.formatString(
          strings.inventoryProfiles.sections.candidates.linkCandidateDialog.message,
          {
            candidateName,
            hiringName
          }
        ),
        confirmButtonText: 'Link profiles',
        cancelButtonText: 'Cancel',
        onConfirm: async ok => {
          if (!ok) return;
          await createHiringAuthority(formData);
        }
      })
    );
  };

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.close,
        variant: 'outlined',
        onClick: onClose
      },
      GridItemProps: {
        md: 3
      }
    },
    {
      props: {
        initialText: strings.shared.ui.save,
        variant: 'contained',
        type: 'submit',
        disabled: uiState.isSaving,
        onProgressText: strings.shared.ui.saving,
        isSaving: uiState.isSaving
      },
      GridItemProps: {
        md: 3
      },
      mode: 'save'
    }
  ];

  return (
    <FPDrawer
      CardHeaderProps={{ title: 'Link Hiring Authority with Candidate' }}
      onClose={onClose}
      ContentWrapper="form"
      ContentWrapperProps={{ onSubmit: handleSubmit(onSubmit) }}
      FPDrawerProps={{
        open: true,
        onClose
      }}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
    >
      <StatusManager
        status={status}
        slots={{
          Empty: (
            <Typography paragraph gutterBottom>
              Hiring Authority not found
            </Typography>
          )
        }}
      >
        {hiringAuthority && (
          <FormContext {...form}>
            <AutocompleteSelect
              name="candidateId"
              displayKey="title"
              selectedValue={comboValues.candidateId}
              error={!!errors.candidateId}
              errorText={errors.candidateId && errors.candidateId.message}
              placeholder="Candidate *"
              typeahead
              typeaheadLimit={50}
              typeaheadParams={{ entityType: EntityType.Candidate }}
              url={Endpoints.Search}
              noOptionsText="Candidate not found"
              onSelect={handleComboChange}
              renderOption={OptionRenderers.candidateInSearch}
            />
          </FormContext>
        )}
        {comboValues?.candidateId && (
          <div className={classes.details}>
            {Renderers.displayBasicInfoEntity(comboValues?.candidateId)}
            {Renderers.externalLink && (
              <div className={classes.link}>
                {Renderers.externalLink(
                  EntityRoutes.CandidateProfile.replace(':id', comboValues?.candidateId.id),
                  'View Candidate'
                )}
              </div>
            )}
          </div>
        )}
      </StatusManager>
    </FPDrawer>
  );
};

LinkCandidate.defaultProps = {
  hiringAuthorityId: null
};

export default LinkCandidate;
